import React, { useCallback } from 'react'
import { Button, message, Table, Upload } from 'antd'
import Icon from '@ant-design/icons'
import type { UploadFile } from 'antd'
import { FormTemplate } from '@icure/cardinal-sdk'
import { Form } from '@icure/form/components/model'
import YAML from 'yaml'

import { CustomModal } from '../../CustomModal'
import { SpinLoader } from '../../SpinLoader'
import {
  useGetFormTemplatesByGuidQuery,
  useCreateFormTemplateVersionMutation,
  useUpdateFormTemplateAttachmentMutation,
  useLazyGetFormTemplateRawAttachmentQuery,
} from '../../../../core/api/formTemplateApi'
import { DEFAULT_MODAL_WIDTH } from '../../../../constants'
import { downloadIcn, uploadIcn, addIcn } from '../../../../assets/CustomIcons'

import './index.less'

interface ModalManageFormTemplatesProps {
  isVisible: boolean
  onClose: () => void
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsText(file)
  })
}

function validateYaml(text: string): boolean {
  try {
    Form.parse(YAML.parse(text))
    return true
  } catch {
    return false
  }
}

export const ModalManageFormTemplates = ({ isVisible, onClose }: ModalManageFormTemplatesProps) => {
  const { data: templates, isLoading } = useGetFormTemplatesByGuidQuery()
  const [createVersion, { isLoading: isCreating }] = useCreateFormTemplateVersionMutation()
  const [updateAttachment, { isLoading: isUpdating }] = useUpdateFormTemplateAttachmentMutation()
  const [triggerGetRawAttachment] = useLazyGetFormTemplateRawAttachmentQuery()

  const loading = isLoading || isCreating || isUpdating

  const handleDownload = useCallback(
    async (templateId: string, templateName: string) => {
      try {
        const result = await triggerGetRawAttachment(templateId).unwrap()
        if (!result) {
          message.error('No attachment found for this template')
          return
        }
        const blob = new Blob([result], { type: 'text/yaml' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${templateName ?? 'template'}.yaml`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        message.success('Template downloaded successfully')
      } catch {
        message.error('Failed to download template')
      }
    },
    [triggerGetRawAttachment],
  )

  const handleUploadReplacement = useCallback(
    async (templateId: string, file: File) => {
      try {
        const text = await readFileAsText(file)
        if (!validateYaml(text)) {
          message.error('Invalid YAML: the file could not be parsed as a valid form template')
          return
        }
        await updateAttachment({ templateId, yamlContent: text }).unwrap()
        message.success('Template attachment updated successfully')
      } catch {
        message.error('Failed to update template attachment')
      }
    },
    [updateAttachment],
  )

  const handleCreateVersion = useCallback(
    async (file: File) => {
      try {
        const text = await readFileAsText(file)
        if (!validateYaml(text)) {
          message.error('Invalid YAML: the file could not be parsed as a valid form template')
          return
        }
        await createVersion({ yamlContent: text }).unwrap()
        message.success('New template version created successfully')
      } catch {
        message.error('Failed to create new template version')
      }
    },
    [createVersion],
  )

  const columns = [
    {
      title: 'Version',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => name ?? 'Unnamed',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: unknown, record: FormTemplate) => (
        <div className="modalManageFormTemplates__actionBtns">
          <Button size="small" icon={<Icon component={downloadIcn} />} onClick={() => handleDownload(record.id, record.name ?? 'template')} title="Download YAML" />
          <Upload
            accept=".yaml,.yml"
            showUploadList={false}
            beforeUpload={(file) => {
              handleUploadReplacement(record.id, file as unknown as File)
              return false
            }}
          >
            <Button size="small" icon={<Icon component={uploadIcn} />} title="Upload replacement" />
          </Upload>
        </div>
      ),
    },
  ]

  return (
    <CustomModal isVisible={isVisible} handleClose={onClose} title="Form templates" width={DEFAULT_MODAL_WIDTH} secondaryBtnTitle="Close">
      <div className="modalManageFormTemplates" style={{ position: 'relative', width: '100%', padding: '16px 24px' }}>
        {loading && <SpinLoader />}
        <div className="modalManageFormTemplates__actions">
          <Upload
            accept=".yaml,.yml"
            showUploadList={false}
            beforeUpload={(file) => {
              handleCreateVersion(file as unknown as File)
              return false
            }}
          >
            <Button type="primary" icon={<Icon component={addIcn} />}>
              Create new version
            </Button>
          </Upload>
        </div>
        <div className="modalManageFormTemplates__table">
          <Table columns={columns} bordered={true} dataSource={templates ?? []} rowKey="id" pagination={false} size="small" locale={{ emptyText: 'No form templates found' }} />
        </div>
      </div>
    </CustomModal>
  )
}
